import {
  Badge,
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import dayjs, { Dayjs } from "dayjs";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import { useTenantTranslation } from "hooks/formatters";
import { useLocalDatetime } from "hooks/useLocalDateTime";
import { SafeDatePicker } from "./SafeDatePicker";
import { PickersDay, PickersDayProps } from "@mui/x-date-pickers/PickersDay";
import { useMemo } from "react";

export interface DayIndicator {
  eventCount?: number;
  statusColour?: string;
}

interface DateStepperProps {
  date: Dayjs;
  setDate: (date: Dayjs) => void;
  highlightedDates?: string[];
  dayIndicators?: Map<string, DayIndicator>;
  indicatorsLoading?: boolean;
  indicatorsError?: boolean;
  onCalendarOpenChange?: (open: boolean) => void;
  onMonthChange?: (month: Dayjs) => void;
  timezone?: string;
}

type HighlightedDayProps = PickersDayProps & {
  highlightedDatesSet?: Set<string>;
  dayIndicators?: Map<string, DayIndicator>;
};

const HighlightedDay = ({
  highlightedDatesSet = new Set(),
  dayIndicators,
  ...props
}: HighlightedDayProps) => {
  const { day, outsideCurrentMonth, ...other } = props;
  const dayKey = day.format("YYYY-MM-DD");
  const isHighlighted = !outsideCurrentMonth && highlightedDatesSet.has(dayKey);
  const indicator = outsideCurrentMonth
    ? undefined
    : dayIndicators?.get(dayKey);
  const eventCount = indicator?.eventCount ?? 0;
  const statusColour = indicator?.statusColour;

  return (
    <Badge
      key={day.toString()}
      overlap="circular"
      badgeContent={isHighlighted ? "●" : undefined}
      sx={{
        "& .MuiBadge-badge": {
          fontSize: "20px",
          padding: 0,
          top: 6,
          right: 6,
          color: "warning.main",
          backgroundColor: "transparent",
        },
      }}
    >
      <Box sx={{ position: "relative" }}>
        <PickersDay
          {...other}
          outsideCurrentMonth={outsideCurrentMonth}
          day={day}
        />
        {eventCount > 0 ? (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              minWidth: 14,
              height: 14,
              px: 0.3,
              borderRadius: "7px",
              bgcolor: "primary.main",
              color: "primary.contrastText",
              fontSize: "9px",
              lineHeight: "14px",
              textAlign: "center",
              pointerEvents: "none",
            }}
          >
            {eventCount}
          </Box>
        ) : null}
        {statusColour ? (
          <Box
            sx={{
              position: "absolute",
              bottom: 2,
              left: 4,
              right: 4,
              height: 2,
              bgcolor: statusColour,
              borderRadius: 1,
              pointerEvents: "none",
            }}
          />
        ) : null}
      </Box>
    </Badge>
  );
};

export const DateStepper = ({
  date,
  setDate,
  highlightedDates,
  dayIndicators,
  indicatorsLoading = false,
  indicatorsError = false,
  onCalendarOpenChange,
  onMonthChange,
  timezone,
}: DateStepperProps) => {
  const { t } = useTenantTranslation();
  const { localTimezone } = useLocalDatetime();
  const tz = timezone ?? localTimezone;
  const todayAtLocalTimezone = dayjs().tz(tz).format("YYYY-MM-DD");

  const highlightedDatesSet = useMemo(
    () =>
      new Set(
        (highlightedDates ?? []).map((d) => dayjs.utc(d).format("YYYY-MM-DD"))
      ),
    [highlightedDates]
  );

  return (
    <Stack>
      <Stack direction={"row"} justifyContent={"center"}>
        <IconButton
          onClick={() => {
            setDate(date.subtract(1, "day"));
          }}
        >
          <ArrowBackIosNewRoundedIcon />
        </IconButton>
        <SafeDatePicker
          localeText={{ start: t("startNoun"), end: t("endNoun") }}
          value={date}
          timezone={tz}
          onChange={(newDate: Dayjs | null) => {
            if (newDate === null) {
              return;
            }
            setDate(newDate);
          }}
          onMonthChange={onMonthChange}
          onOpen={() => onCalendarOpenChange?.(true)}
          onClose={() => onCalendarOpenChange?.(false)}
          displayWeekNumber
          shouldDisableDate={(dateToEvaluate) => {
            return (
              dateToEvaluate.tz(tz).format("YYYY-MM-DD") > todayAtLocalTimezone
            );
          }}
          slots={{
            day: HighlightedDay,
          }}
          slotProps={{
            day: { highlightedDatesSet, dayIndicators } as object,
          }}
        />
        <IconButton
          onClick={() => {
            setDate(date.add(1, "day"));
          }}
          disabled={
            date.add(1, "day").tz(tz).format("YYYY-MM-DD") >
            todayAtLocalTimezone
          }
        >
          <ArrowForwardIosRoundedIcon />
        </IconButton>
        <Box
          sx={{
            width: 20,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {indicatorsLoading ? (
            <CircularProgress size={14} />
          ) : indicatorsError ? (
            <Tooltip title={t("errorLoadingData")}>
              <ErrorOutlineIcon
                color="error"
                fontSize="small"
                titleAccess={t("errorLoadingData")}
              />
            </Tooltip>
          ) : null}
        </Box>
      </Stack>
    </Stack>
  );
};
